"use client";
/**
 * SANATAN DECODE — गोत्र, Episode 1.  v18
 *
 * v17 was rejected outright ("worse than a 2005 powerpoint"). Three causes, all fixed here:
 *   1. ⛔ THE WTS WAS WRONG. v17 used ASR-windowed alignment; at t=60s it crammed 23 words into
 *      4 seconds (390 wpm). Now driven by gotraTiming.ts built from a GLOBAL CTC forced
 *      alignment (align_wts2.py), verified against re-transcribed audio at three points.
 *   2. ⛔ PROPS OVERLAPPED. Positions were hand-typed left/top with guessed widths. Now every
 *      prop is packed by `Row`, which reads real pixel dimensions from assetDims.ts and computes
 *      x from the actual widths — collision is arithmetically impossible.
 *   3. ⛔ IT WAS BLANK. One or two props per beat over a blurred plate. Now props ACCUMULATE
 *      within a beat instead of swapping, and the text column is a fixed zone the props can
 *      never enter.
 *
 * Also fixed: the hook speaks SIX occasions (जन्म, मुंडन, शादी, गृह प्रवेश, हवन, अंतिम संस्कार)
 * and v17 showed five, with हवन standing in for जन्म. Six now, each landing on its own word.
 */
import React from "react";
import {
  AbsoluteFill, Audio, Img, staticFile, interpolate, Easing, random,
  useVideoConfig, useCurrentFrame, spring,
} from "remotion";
import { deva, INK, GOLD, KUMKUM, MUTED_C, CREAM, Grain, PROP_SHADOW } from "./sanatan/kit";
import { SEG, PH } from "./sanatan/gotraTiming";
import { wAt } from "./sanatan/assetDims";

/* ── fps-agnostic timing (the kit hard-codes 30) ───────────────
   LAW 1 targets 12-15fps motion, not the literal number 2: step 2 at 30fps, step 1 at 16fps. */
const useSec = () => { const { fps } = useVideoConfig(); return (s: number) => s * fps; };
const useStepF = () => {
  const { fps } = useVideoConfig();
  const step = Math.max(1, Math.round(fps / 15));
  return Math.floor(useCurrentFrame() / step) * step;
};
const useSpringAtF = (at: number, damping = 9, stiffness = 110, mass = 1.1) => {
  const { fps } = useVideoConfig();
  return spring({ frame: useStepF() - at * fps, fps, config: { damping, stiffness, mass } });
};

const CUT = (n: string) => staticFile(`sanatan/cut/${n}.png`);
const PHO = (n: string) => staticFile(`sanatan/photo/${n}.png`);
const PANOS = ["gurukul_pano", "pano_sabha", "modern_pano", "pano_courtroom"];

/* ── THE ZONES. Text lives left, props live right. They never share space. ── */
const TEXT_X = 54, TEXT_W = 520;
const PROP_X = 610, PROP_W = 1280 - PROP_X - 30;

type BeatDef = { segs: [number, number]; g: number; x: number; tint?: string; op?: number };
const B: BeatDef[] = [
  { segs: [0, 1],   g: 0, x: 0.00 }, { segs: [2, 2],   g: 0, x: 0.08 },
  { segs: [3, 4],   g: 0, x: 0.18 }, { segs: [5, 6],   g: 0, x: 0.30 },
  { segs: [7, 8],   g: 1, x: 0.00, tint: "#C9A24B", op: 0.20 },
  { segs: [9, 12],  g: 1, x: 0.14, tint: "#C9A24B", op: 0.16 },
  { segs: [13, 15], g: 0, x: 0.42 }, { segs: [16, 17], g: 0, x: 0.54 },
  { segs: [18, 18], g: 0, x: 0.64 },
  { segs: [19, 23], g: 1, x: 0.32, tint: "#C9A24B", op: 0.20 },
  { segs: [24, 26], g: 1, x: 0.48, tint: "#C9A24B", op: 0.20 },
  { segs: [27, 30], g: 0, x: 0.74 }, { segs: [31, 32], g: 0, x: 0.86 },
  { segs: [33, 34], g: 0, x: 0.96 }, { segs: [35, 36], g: 0, x: 0.84 },
  { segs: [37, 39], g: 0, x: 0.70 }, { segs: [40, 41], g: 0, x: 0.58 },
  { segs: [42, 43], g: 2, x: 0.00, tint: "#7FA6B8", op: 0.18 },
  { segs: [44, 46], g: 0, x: 0.46 },
  { segs: [47, 49], g: 2, x: 0.26, tint: "#7FA6B8", op: 0.18 },
  { segs: [50, 50], g: 2, x: 0.44, tint: "#7FA6B8", op: 0.18 },
  { segs: [51, 52], g: 3, x: 0.10, tint: "#8C9C7A", op: 0.22 },
  { segs: [53, 55], g: 0, x: 0.34 }, { segs: [56, 57], g: 0, x: 0.22 },
  { segs: [58, 61], g: 0, x: 0.10 },
];
const bt = (i: number): [number, number] => [SEG[B[i].segs[0]].start, SEG[B[i].segs[1]].end];
const ph = (k: string): [number, number] => (PH[k] ? [PH[k][0], PH[k][1]] : [1e6, 1e6]);

/* ══ GROUND — held still, travels only across a cut ══ */
const PANO_W = 3840, PANO_H = 1280, DISPLAY_H = 980;
const Ground: React.FC = () => {
  const frame = useStepF(); const F = useSec();
  const dispW = (PANO_W / PANO_H) * DISPLAY_H, travel = dispW - 1280;
  const ts: number[] = [], xs: number[] = [];
  B.forEach((b, i) => { const [a, z] = bt(i); ts.push(F(a), F(z)); xs.push(b.x, b.x); });
  for (let i = 1; i < ts.length; i++) if (ts[i] <= ts[i - 1]) ts[i] = ts[i - 1] + 1;
  const x = interpolate(frame, ts, xs, { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic) });
  let gi = 0, tint = "", top = 0;
  for (let i = 0; i < B.length; i++) if (frame >= F(bt(i)[0]) - 8) { gi = B[i].g; tint = B[i].tint ?? ""; top = B[i].op ?? 0; }
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden", zIndex: 0 }}>
      <Img src={staticFile(`sanatan/pano/${PANOS[gi]}.png`)} style={{
        position: "absolute", height: DISPLAY_H, width: dispW, left: -x * travel, top: -150,
        maxWidth: "none", filter: "blur(4.6px) saturate(0.58) brightness(1.14) contrast(0.82)",
      }} />
      <AbsoluteFill style={{ backgroundColor: "rgba(249,240,222,0.40)" }} />
      {tint && <AbsoluteFill style={{ backgroundColor: tint, opacity: top, mixBlendMode: "multiply" }} />}
      <AbsoluteFill style={{ background: "radial-gradient(ellipse 86% 80% at 46% 48%, rgba(255,252,242,0.34) 0%, rgba(198,158,100,0.36) 100%)" }} />
    </AbsoluteFill>
  );
};

/* ══ PROP — one image, swings in on its own word, idles, leaves with the beat ══ */
const Prop: React.FC<{ src: string; h: number; left: number; top: number; inAt: number; outAt: number; from?: 1 | -1; z?: number }> =
  ({ src, h, left, top, inAt, outAt, from = 1, z = 12 }) => {
    const frame = useStepF(); const F = useSec();
    const sIn = useSpringAtF(inAt, 8, 95, 1.15);
    const sOut = useSpringAtF(outAt, 200, 75, 0.5);
    const pend = Math.sin((frame - F(inAt)) / 34) * 1.3 * sIn * (1 - sOut);
    const dx = 230 * from * (1 - sIn) - 330 * from * sOut;
    const dy = 64 * (1 - sIn) + 180 * sOut;
    if (frame < F(inAt) - 34 || frame > F(outAt) + 40) return null;
    return (
      <Img src={src} style={{
        position: "absolute", left, top, height: h, zIndex: z, filter: PROP_SHADOW,
        transform: `translate(${dx}px, ${dy}px) rotate(${13 * from * (1 - sIn) + pend - 26 * from * sOut}deg) scale(${0.9 + 0.1 * sIn - 0.12 * sOut})`,
        transformOrigin: "50% -26%",
        opacity: Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4)),
      }} />
    );
  };

/* ══ ROW — packs props by their REAL widths. Overlap is arithmetically impossible. ══
   Each item may carry its own `at` so props ACCUMULATE on their own spoken words. */
type Item = { k: string; h: number; at?: number; photo?: boolean };
const Row: React.FC<{ items: Item[]; outAt: number; baseline?: number; zoneX?: number; zoneW?: number; gap?: number; inAt?: number }> =
  ({ items, outAt, baseline = 620, zoneX = PROP_X, zoneW = PROP_W, gap = 22, inAt = 0 }) => {
    const ws = items.map((it) => wAt((it.photo ? "photo/" : "cut/") + it.k, it.h));
    const total = ws.reduce((a, b) => a + b, 0) + gap * (items.length - 1);
    const scale = total > zoneW ? zoneW / total : 1;          // shrink to fit, never spill
    let x = zoneX + (zoneW - total * scale) / 2;
    return (
      <>
        {items.map((it, i) => {
          const h = it.h * scale, w = ws[i] * scale;
          const left = x; x += w + gap * scale;
          return (
            <Prop key={it.k + i} src={it.photo ? PHO(it.k) : CUT(it.k)} h={h}
              left={left} top={baseline - h} inAt={it.at ?? inAt} outAt={outAt}
              from={i % 2 === 0 ? 1 : -1} z={12 + i} />
          );
        })}
      </>
    );
  };

/* ══ TYPE ══ */
const Phrase: React.FC<{ k: string; size?: number; color?: string; y?: number; hold?: number; x?: number; w?: number }> =
  ({ k, size = 58, color = INK, y = 96, hold = 2.4, x = TEXT_X, w = TEXT_W }) => {
    const p = PH[k];
    const sIn = useSpringAtF(p ? p[0] : 1e6, 9, 150, 0.8);
    const sOut = useSpringAtF(p ? p[1] + hold : 1e6, 200, 80, 0.5);
    if (!p) return null;
    return (
      <div style={{
        position: "absolute", left: x, top: y, width: w,
        fontFamily: deva, fontWeight: 800, fontSize: size, lineHeight: 1.28, color,
        textShadow: "0 0 28px rgba(255,251,240,0.99), 0 0 11px rgba(255,251,240,0.97), 0 2px 2px rgba(255,251,240,0.9)",
        opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
        transform: `translateY(${(1 - sIn) * 32 + sOut * 42}px) rotate(${4 * (1 - sIn)}deg)`,
        transformOrigin: "0% 140%",
      }}>{p[2]}</div>
    );
  };

const Tag: React.FC<{ text: string; at: number; out: number; y: number; size?: number; color?: string; x?: number }> =
  ({ text, at, out, y, size = 30, color = MUTED_C, x = TEXT_X }) => {
    const sIn = useSpringAtF(at, 9, 160, 0.8);
    const sOut = useSpringAtF(out, 200, 80, 0.5);
    return (
      <div style={{
        position: "absolute", left: x, top: y, width: TEXT_W,
        fontFamily: deva, fontWeight: 700, fontSize: size, lineHeight: 1.35, color,
        textShadow: "0 0 24px rgba(255,251,240,0.99), 0 0 10px rgba(255,251,240,0.96)",
        opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
        transform: `translateY(${(1 - sIn) * 20 + sOut * 36}px)`,
      }}>{text}</div>
    );
  };

/* ══ THE MAP — v14's win, drawn organic ══ */
const node = (g: number, i: number, n: number, w: number, h: number) => ({
  x: (w * (i + 1)) / (n + 1) + (random(`x${g}-${i}`) - 0.5) * (w / (n + 1)) * 0.6,
  y: 26 + g * (h / 5.2) + (random(`y${g}-${i}`) - 0.5) * 32,
});
const Map: React.FC<{ gens: number; lit?: number[]; w?: number; h?: number; at: number; out: number }> =
  ({ gens, lit = [], w = 560, h = 400, at, out }) => {
    const sIn = useSpringAtF(at, 10, 120, 1.0);
    const sOut = useSpringAtF(out, 200, 80, 0.5);
    const paths: React.ReactNode[] = [], dots: React.ReactNode[] = [];
    for (let g = 0; g < gens; g++) {
      const n = Math.min(2 ** g, 12);
      for (let i = 0; i < n; i++) {
        const p = node(g, i, n, w, h);
        if (g > 0) {
          const pn = Math.min(2 ** (g - 1), 12);
          const q = node(g - 1, Math.floor(i / 2) % pn, pn, w, h);
          paths.push(<path key={`p${g}${i}`} d={`M ${q.x} ${q.y} Q ${(p.x + q.x) / 2 + (random(`c${g}${i}`) - 0.5) * 28} ${(p.y + q.y) / 2} ${p.x} ${p.y}`}
            fill="none" stroke={INK} strokeWidth={1.8} strokeLinecap="round" opacity={0.44} />);
        }
        const on = g === gens - 1 && lit.includes(i);
        dots.push(<circle key={`d${g}${i}`} cx={p.x} cy={p.y} r={on ? 9 : 5}
          fill={on ? KUMKUM : CREAM} stroke={on ? KUMKUM : INK} strokeWidth={on ? 2 : 1.6} />);
      }
    }
    const o = Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4));
    return (
      <svg width={w} height={h} style={{
        position: "absolute", left: PROP_X + (PROP_W - w) / 2, top: 150, overflow: "visible",
        opacity: o, transform: `translateY(${(1 - sIn) * 26}px) scale(${0.94 + sIn * 0.06})`,
        filter: "drop-shadow(0 6px 14px rgba(255,251,240,0.9))",
      }}>{paths}{dots}</svg>
    );
  };

const Beat: React.FC<{ i: number; children: React.ReactNode }> = ({ i, children }) => {
  const frame = useStepF(); const F = useSec();
  const [a, z] = bt(i);
  if (frame < F(a - 0.7) || frame >= F(z + 0.9)) return null;
  return <AbsoluteFill style={{ zIndex: 10 }}>{children}</AbsoluteFill>;
};

/* ══ DYNAMIC FACTOR — author once at 1280×720, ship any size ══ */
const AW = 1280, AH = 720;
const Stage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { width, height } = useVideoConfig();
  const k = Math.max(width / AW, height / AH);
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden" }}>
      <div style={{ position: "absolute", width: AW, height: AH, left: (width - AW * k) / 2, top: (height - AH * k) / 2, transform: `scale(${k})`, transformOrigin: "0 0" }}>
        {children}
      </div>
    </AbsoluteFill>
  );
};

export const SanatanDecodeGotraV18: React.FC = () => <Stage><Film /></Stage>;

const Film: React.FC = () => {
  const E = (i: number) => bt(i)[1] - 0.25;
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC" }}>
      <Audio src={staticFile("sanatan/audio/gotra_ep01.mp3")} />
      <Ground />

      {/* 0 · HOOK — SIX occasions, each on its own spoken word, ACCUMULATING */}
      <Beat i={0}>
        <Row outAt={ph("hook")[0] - 0.5} baseline={640} zoneX={40} zoneW={1200} gap={16}
          items={[
            { k: "bi_cradle",    h: 170, at: ph("o_janam")[0] },
            { k: "rt_razor",     h: 140, at: ph("o_mundan")[0] },
            { k: "rt_mandap",    h: 185, at: ph("o_shadi")[0] },
            { k: "rt_threshold", h: 150, at: ph("o_griha")[0] },
            { k: "rt_havan",     h: 175, at: ph("o_havan")[0] },
            { k: "rt_pot",       h: 150, at: ph("o_antim")[0] },
          ]} />
        <Row outAt={E(0)} inAt={ph("hook")[0] - 0.9} baseline={660} zoneX={700} zoneW={540}
          items={[{ k: "pp_pandit", h: 330 }]} />
        <Phrase k="hook" size={66} color={KUMKUM} y={120} w={560} />
      </Beat>

      {/* 1 · SETUP */}
      <Beat i={1}>
        <Row outAt={E(1)} inAt={bt(1)[0]} baseline={650} items={[{ k: "pp_phoneman", h: 340 }, { k: "pp_elder", h: 300 }]} />
        <Phrase k="setup" size={60} y={110} />
        <Tag text="गोत्र · प्रवर · सपिंड" at={ph("setup")[0] + 0.7} out={E(1)} y={230} size={38} color={GOLD} />
      </Beat>

      {/* 2 · TWO SYSTEMS */}
      <Beat i={2}>
        <Map gens={3} at={bt(2)[0] + 0.4} out={E(2)} />
        <Phrase k="two" size={58} y={110} />
        <Tag text="आर्ष — रिषि से" at={ph("arsha")[0]} out={E(2)} y={250} size={34} color={KUMKUM} />
        <Tag text="लौकिक — लोक से" at={ph("arsha")[0] + 1.3} out={E(2)} y={300} size={34} color={GOLD} />
      </Beat>

      {/* 3 · ⭐ THE RISHI CHAIN */}
      <Beat i={3}>
        <Row outAt={E(3)} inAt={bt(3)[0] + 0.3} baseline={660}
          items={[{ k: "pp_fatherson", h: 330 }, { k: "vd_sage_seated", h: 300, at: ph("define")[0] + 0.4 }]} />
        <Phrase k="define" size={52} y={110} />
        <Tag text="पिता → पिता → पिता →" at={ph("define")[0] + 1.1} out={E(3)} y={260} size={34} />
      </Beat>

      {/* 4 · LAUKIK */}
      <Beat i={4}>
        <Row outAt={E(4)} inAt={bt(4)[0] + 0.2} baseline={660}
          items={[{ k: "pp_genealogist", h: 340 }, { k: "cl_fort", h: 190 }]} />
        <Phrase k="laukik" size={64} color={GOLD} y={120} />
        <Tag text="पुरखा · गाँव · काम · वंश" at={ph("laukik")[0] + 1.5} out={E(4)} y={240} size={34} />
      </Beat>

      {/* 5 · THREE CLARIFIERS */}
      <Beat i={5}>
        <Row outAt={E(5)} inAt={bt(5)[0] + 0.3} baseline={660}
          items={[{ k: "modern_man", h: 320 }, { k: "pp_fatherson", h: 300, at: ph("caste")[0] - 1.0 }]} />
        <Phrase k="surname" size={56} color={KUMKUM} y={120} />
        <Phrase k="caste" size={50} y={280} />
      </Beat>

      {/* 6 · BAUDHAYANA */}
      <Beat i={6}>
        <Row outAt={E(6)} inAt={bt(6)[0] + 0.2} baseline={665}
          items={[{ k: "vd_sage_seated", h: 280 }, { k: "vd_sage_staff", h: 320, at: ph("agastya")[0] - 0.6 },
                  { k: "vd_manuscript_open", h: 190, at: bt(6)[0] + 2.2 }]} />
        <Phrase k="seven" size={68} y={110} />
        <Phrase k="agastya" size={48} color={GOLD} y={220} />
      </Beat>

      {/* 7 · THE COUNTS EXPLODE */}
      <Beat i={7}>
        <Map gens={5} w={620} h={440} at={bt(7)[0] + 0.3} out={E(7)} />
        <Phrase k="counts" size={72} color={KUMKUM} y={210} />
      </Beat>

      {/* 8 · SHAKHA */}
      <Beat i={8}>
        <Map gens={4} lit={[2, 5]} at={bt(8)[0] + 0.2} out={E(8)} />
        <Phrase k="shakha" size={52} y={170} />
      </Beat>

      {/* 9 · THE REAL LIST */}
      <Beat i={9}>
        <Row outAt={E(9)} inAt={bt(9)[0] + 0.4} baseline={660}
          items={[{ k: "rc_register", h: 330, photo: true },
                  { k: "cl_goldsmith", h: 190, at: ph("kataria")[0] - 0.8 },
                  { k: "cl_saltlake", h: 150, at: ph("kataria")[0] + 0.6 }]} />
        <Phrase k="columns" size={40} y={110} />
        <Phrase k="kataria" size={58} color={KUMKUM} y={290} />
        <Phrase k="purkha" size={42} color={GOLD} y={420} />
      </Beat>

      {/* 10 · NO RISHI COLUMN */}
      <Beat i={10}>
        <Row outAt={E(10)} inAt={bt(10)[0]} baseline={660}
          items={[{ k: "rc_register", h: 300, photo: true }, { k: "cl_shrine", h: 220, at: bt(10)[0] + 1.6 }]} />
        <Tag text="यह दूसरी तरह की सूची है" at={bt(10)[0] + 1.0} out={E(10)} y={200} size={44} color={INK} />
      </Beat>

      {/* 11 · प्रवर + भीष्म */}
      <Beat i={11}>
        <Row outAt={E(11)} inAt={bt(11)[0] + 0.3} baseline={660}
          items={[{ k: "pp_pandit", h: 300 }, { k: "vd_manuscript_open", h: 200, at: ph("bhishma")[0] - 1.4 }]} />
        <Phrase k="pravar" size={70} color={KUMKUM} y={110} />
        <Phrase k="bhishma" size={40} color={GOLD} y={430} />
      </Beat>

      {/* 12 · WHY 49 */}
      <Beat i={12}>
        <Row outAt={E(12)} inAt={bt(12)[0] + 0.2} baseline={660}
          items={[{ k: "vd_yajna", h: 280 }, { k: "vd_manuscript_open", h: 180 }]} />
        <Phrase k="fortynine" size={54} y={120} />
        <Phrase k="diff" size={42} color={KUMKUM} y={300} />
      </Beat>

      {/* 13 · सपिंड */}
      <Beat i={13}>
        <Row outAt={E(13)} inAt={bt(13)[0] + 0.3} baseline={655}
          items={[{ k: "couple", h: 300 }, { k: "bi_mother", h: 280, at: ph("sapind")[0] + 1.8 }]} />
        <Phrase k="sapind" size={52} y={130} />
      </Beat>

      {/* 14 · SANKALPA — the same havan returns */}
      <Beat i={14}>
        <Row outAt={E(14)} inAt={bt(14)[0] + 0.2} baseline={655}
          items={[{ k: "rt_havan", h: 250 }, { k: "vd_yajna", h: 250 }]} />
        <Phrase k="sankalpa" size={50} y={150} />
      </Beat>

      {/* 15 · MARRIAGE */}
      <Beat i={15}>
        <Row outAt={E(15)} inAt={bt(15)[0] + 0.3} baseline={655}
          items={[{ k: "rt_mandap", h: 230 }, { k: "couple", h: 270, at: bt(15)[0] + 1.1 }]} />
        <Phrase k="three" size={58} color={KUMKUM} y={160} />
      </Beat>

      {/* 16 · KANE */}
      <Beat i={16}>
        <Map gens={3} lit={[0, 1, 2, 3]} at={bt(16)[0] + 0.3} out={E(16)} />
        <Phrase k="kane" size={48} y={160} />
      </Beat>

      {/* 17 · SCIENCE OPENS */}
      <Beat i={17}>
        <Row outAt={E(17)} inAt={bt(17)[0] + 0.2} baseline={655}
          items={[{ k: "modern_man", h: 310 }, { k: "dna", h: 330, at: bt(17)[0] + 1.2 }]} />
        <Phrase k="dnaclaim" size={48} y={120} />
      </Beat>

      {/* 18 · शास्त्र SIDE */}
      <Beat i={18}>
        <Row outAt={E(18)} inAt={bt(18)[0] + 0.4} baseline={655}
          items={[{ k: "ph_manuscript", h: 210, photo: true }, { k: "vd_sage_seated", h: 270, at: ph("adharma")[0] - 1.2 }]} />
        <Phrase k="charak" size={50} color={GOLD} y={130} />
        <Phrase k="adharma" size={44} color={KUMKUM} y={300} />
      </Beat>

      {/* 19 · ⭐ SCIENCE SIDE */}
      <Beat i={19}>
        <Row outAt={E(19)} inAt={ph("risk")[0] - 1.3} baseline={650}
          items={[{ k: "sc_bars", h: 240 }, { k: "sc_carriers", h: 240, at: ph("maternal")[0] - 0.9 },
                  { k: "couple", h: 250, at: ph("tests")[0] - 1.0 }]} />
        <Phrase k="risk" size={54} color={KUMKUM} y={110} />
        <Phrase k="maternal" size={40} y={270} />
        <Phrase k="tests" size={42} color={GOLD} y={410} />
      </Beat>

      {/* 20 · Y CHROMOSOME */}
      <Beat i={20}>
        <Row outAt={E(20)} inAt={bt(20)[0] + 0.2} baseline={655}
          items={[{ k: "sc_ychrom", h: 300 }, { k: "dna", h: 290 }]} />
        <Phrase k="ychrom" size={62} color={KUMKUM} y={200} />
      </Beat>

      {/* 21 · THE LAW */}
      <Beat i={21}>
        <Row outAt={E(21)} inAt={bt(21)[0] + 0.3} baseline={655}
          items={[{ k: "rc_statute", h: 250, photo: true }, { k: "rc_files", h: 200, photo: true, at: bt(21)[0] + 1.2 }]} />
        <Phrase k="legal" size={50} color={KUMKUM} y={120} />
        <Phrase k="lokachar" size={44} color={GOLD} y={330} />
      </Beat>

      {/* 22 · CLOSE */}
      <Beat i={22}>
        <Row outAt={E(22)} inAt={bt(22)[0] + 0.3} baseline={655}
          items={[{ k: "pp_pandit", h: 290 }, { k: "pp_phoneman", h: 310, at: bt(22)[0] + 1.0 }]} />
        <Phrase k="answer" size={66} color={KUMKUM} y={140} />
        <Phrase k="kashyap" size={48} color={GOLD} y={300} />
      </Beat>

      {/* 23 · सत्यकाम */}
      <Beat i={23}>
        <Row outAt={E(23)} inAt={bt(23)[0] + 0.4} baseline={655}
          items={[{ k: "vd_sage_seated", h: 300 }, { k: "bi_mother", h: 280, at: bt(23)[0] + 1.4 }]} />
        <Phrase k="satyakam" size={64} color={KUMKUM} y={190} />
      </Beat>

      {/* 24 · OPEN QUESTION + CTA + SIGN-OFF */}
      <Beat i={24}>
        <Row outAt={E(24)} inAt={bt(24)[0] + 0.4} baseline={655}
          items={[{ k: "pp_elder", h: 280 }, { k: "cl_garland", h: 200, at: ph("signoff")[0] - 1.5 }]} />
        <Phrase k="openq" size={42} y={120} />
        <Phrase k="cta" size={48} color={GOLD} y={290} />
        <Phrase k="signoff" size={74} color={KUMKUM} y={430} />
      </Beat>

      <Grain op={0.12} />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
